/* ============================================================
   VITALCASA CRM — Clientes
   Lista de clientes con tabla configurable, tema claro/oscuro.
   ============================================================ */
import { useState, useEffect, useRef } from "react";
import { useReloadOnRouteEnter } from "../hooks/useReloadOnRouteEnter";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../components/ThemeProvider";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import { textIncludes, textIncludesAny } from "../utils/searchText";

const TODAS_COLUMNAS = [
  { id: "select",          label: "",              width: 40,  fija: true },
  { id: "estado",          label: "Estado",        width: 90,  fija: true },
  { id: "nombre_completo", label: "Nombre completo", width: 200, fija: true },
  { id: "telefono",        label: "Teléfono",      width: 120, fija: true },
  { id: "tipo_operacion",  label: "Tipo operación", width: 120 },
  { id: "procedencia",     label: "Procedencia",   width: 120 },
  { id: "fecha_alta",      label: "Fecha alta",    width: 110, fija: true },
  { id: "fecha_ult_lead",  label: "Fecha últ. lead", width: 120 },
  { id: "agente",          label: "Agente",        width: 150 },
  { id: "email",           label: "Mail",          width: 200 },
  { id: "idioma",          label: "Idioma",        width: 80 },
  { id: "direccion",       label: "Dirección",     width: 200 },
  { id: "poblacion",       label: "Población",     width: 120 },
  { id: "codigo_postal",   label: "C.P.",          width: 70 },
  { id: "provincia",       label: "Provincia",     width: 110 },
  { id: "pais",            label: "País",          width: 100 },
  { id: "dni",             label: "DNI",           width: 110 },
];

const COL_STORAGE_KEY = "vitalcasa_clientes_cols";

function cargarConfigColumnas() {
  try {
    const saved = localStorage.getItem(COL_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      const ids = parsed.map(c => c.id);
      TODAS_COLUMNAS.forEach(c => {
        if (!ids.includes(c.id)) parsed.push({ id: c.id, visible: true, width: c.width });
      });
      return parsed;
    }
  } catch (e) {}
  return TODAS_COLUMNAS.map(c => ({ id: c.id, visible: true, width: c.width }));
}

function guardarConfigColumnas(config) {
  try { localStorage.setItem(COL_STORAGE_KEY, JSON.stringify(config)); } catch (e) {}
}

// ── Badge de estado con color ──────────────────────────────
function BadgeEstado({ estado, isDark }) {
  const colores = {
    activo:     { bg: "#16a34a20", color: "#16a34a", border: "#16a34a40" },
    inactivo:   { bg: "#ef444420", color: "#ef4444", border: "#ef444440" },
    potencial:  { bg: "#f59e0b20", color: "#f59e0b", border: "#f59e0b40" },
    contactado: { bg: "#3b82f620", color: "#3b82f6", border: "#3b82f640" },
  };
  const c = colores[estado?.toLowerCase()] || colores.activo;
  return (
    <span style={{
      fontSize: 11, fontWeight: 600, padding: "3px 10px",
      borderRadius: 20, background: c.bg, color: c.color,
      border: `1px solid ${c.border}`,
    }}>
      {estado || "Activo"}
    </span>
  );
}

export default function Clientes() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { usuario } = useAuth();
  const t = theme.colors;
  const isDark = theme.name === "dark";

  const [clientes, setClientes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [busqueda, setBusqueda] = useState("");
  const [seleccionadas, setSeleccionadas] = useState([]);
  const [colConfig, setColConfig] = useState(cargarConfigColumnas());
  const [mostrarColConfig, setMostrarColConfig] = useState(false);
  const [resizing, setResizing] = useState(null);
  const [dragCol, setDragCol] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [mensaje, setMensaje] = useState("");

  const startX = useRef(0);
  const startW = useRef(0);

  useReloadOnRouteEnter(() => {
    cargar();
  });

  const cargar = async () => {
    try {
      const res = await api.get("/clientes");
      setClientes(res.data.data || []);
    } catch (err) {
      console.error("Error cargando clientes:", err);
    } finally {
      setCargando(false);
    }
  };

  // ── Filtro ───────────────────────────────────────────────
  const filtrados = clientes.filter((c) => {
    if (!busqueda.trim()) return true;
    return (
      textIncludes(`${c.nombre || ""} ${c.apellido || ""}`, busqueda) ||
      textIncludesAny([c.email, c.telefono, c.poblacion, c.dni], busqueda)
    );
  });

  // ── Selección ────────────────────────────────────────────
  const toggleSeleccion = (id) => {
    setSeleccionadas(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleTodas = () => {
    if (seleccionadas.length === filtrados.length) setSeleccionadas([]);
    else setSeleccionadas(filtrados.map(c => c.id));
  };

  // ── Columnas ─────────────────────────────────────────────
  const toggleColumna = (colId) => {
    const nueva = colConfig.map(c => c.id === colId ? { ...c, visible: !c.visible } : c);
    setColConfig(nueva);
    guardarConfigColumnas(nueva);
  };

  const onDragStartCol = (e, colId) => {
    setDragCol(colId);
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOverCol = (e, colId) => {
    e.preventDefault();
    if (colId !== dragCol) setDragOver(colId);
  };
  const onDropCol = (colId) => {
    if (!dragCol || dragCol === colId) { setDragCol(null); setDragOver(null); return; }
    const nueva = [...colConfig];
    const fromIdx = nueva.findIndex(c => c.id === dragCol);
    const toIdx = nueva.findIndex(c => c.id === colId);
    const [moved] = nueva.splice(fromIdx, 1);
    nueva.splice(toIdx, 0, moved);
    setColConfig(nueva);
    guardarConfigColumnas(nueva);
    setDragCol(null);
    setDragOver(null);
  };

  // ── Resize columnas ──────────────────────────────────────
  const onMouseDown = (colId, e) => {
    e.preventDefault();
    const colDef = TODAS_COLUMNAS.find(c => c.id === colId);
    if (colDef?.fija) return;
    startX.current = e.clientX;
    const col = colConfig.find(c => c.id === colId);
    startW.current = col?.width || 100;
    setResizing(colId);
  };

  useEffect(() => {
    if (!resizing) return;
    const onMove = (e) => {
      const diff = e.clientX - startX.current;
      const newW = Math.max(40, startW.current + diff);
      const nueva = colConfig.map(c => c.id === resizing ? { ...c, width: newW } : c);
      setColConfig(nueva);
    };
    const onUp = () => {
      setResizing(null);
      guardarConfigColumnas(colConfig);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => { document.removeEventListener("mousemove", onMove); document.removeEventListener("mouseup", onUp); };
  }, [resizing, colConfig]);

  const columnasVisibles = colConfig
    .filter(cc => cc.visible !== false)
    .map(cc => TODAS_COLUMNAS.find(c => c.id === cc.id))
    .filter(Boolean);

  const getWidth = (colId) => {
    const colDef = TODAS_COLUMNAS.find(c => c.id === colId);
    const conf = colConfig.find(c => c.id === colId);
    if (colDef?.fija) return conf?.width || colDef.width;
    return conf?.width || colDef?.width || 100;
  };

  const calcularEstilo = (colId) => {
    const colDef = TODAS_COLUMNAS.find(c => c.id === colId);
    const w = getWidth(colId);
    if (colDef?.fija) return { width: w, minWidth: w, maxWidth: w };
    return { minWidth: w };
  };

  const formatFecha = (f) => {
    if (!f) return "-";
    return new Date(f).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
  };

  // ── Render celdas ────────────────────────────────────────
  const renderCelda = (c, colId) => {
    switch (colId) {
      case "select":
        return <input type="checkbox" checked={seleccionadas.includes(c.id)} onChange={() => toggleSeleccion(c.id)} className="rounded" />;
      case "estado":
        return <BadgeEstado estado={c.estado} isDark={isDark} />;
      case "nombre_completo":
        return <span className="text-sm font-medium" style={{ color: t.textPrimary }}>{(c.nombre || "") + " " + (c.apellido || "")}</span>;
      case "telefono":
        return (
          <a href={"tel:" + c.telefono} className="text-sm" style={{ color: isDark ? "#93c5fd" : t.accentBlue, textDecoration: "none" }}
            onClick={e => e.stopPropagation()}>
            {c.telefono || "-"}
          </a>
        );
      case "tipo_operacion":
        return <span className="text-sm" style={{ color: t.textPrimary }}>{c.tipo_operacion || "-"}</span>;
      case "procedencia":
        return <span className="text-sm" style={{ color: t.textPrimary }}>{c.procedencia || "-"}</span>;
      case "fecha_alta":
        return <span className="text-sm" style={{ color: t.textPrimary }}>{formatFecha(c.created_at)}</span>;
      case "fecha_ult_lead":
        return <span className="text-sm" style={{ color: t.textPrimary }}>{formatFecha(c.fecha_ultimo_lead)}</span>;
      case "agente":
        return <span className="text-sm" style={{ color: t.textPrimary }}>{c.agente_nombre || "-"}</span>;
      case "email":
        return (
          <a href={"mailto:" + c.email} className="text-sm" style={{ color: isDark ? "#93c5fd" : t.accentBlue, textDecoration: "none" }}
            onClick={e => e.stopPropagation()}>
            {c.email || "-"}
          </a>
        );
      case "idioma":
        return <span className="text-sm" style={{ color: t.textPrimary }}>{c.idioma || "-"}</span>;
      case "direccion":
        return <span className="text-sm" style={{ color: t.textPrimary }}>{c.direccion || "-"}</span>;
      case "poblacion":
        return <span className="text-sm" style={{ color: t.textPrimary }}>{c.poblacion || "-"}</span>;
      case "codigo_postal":
        return <span className="text-sm" style={{ color: t.textPrimary }}>{c.codigo_postal || "-"}</span>;
      case "provincia":
        return <span className="text-sm" style={{ color: t.textPrimary }}>{c.provincia || "-"}</span>;
      case "pais":
        return <span className="text-sm" style={{ color: t.textPrimary }}>{c.pais || "-"}</span>;
      case "dni":
        return <span className="text-sm" style={{ color: t.textPrimary }}>{c.dni || "-"}</span>;
      default:
        return "-";
    }
  };

  return (
    <div className="min-h-screen" style={{ background: t.bgApp }}>
      <header className="px-6 py-4 flex items-center justify-between" style={{ background: t.bgCard, borderBottom: `1px solid ${t.border}` }}>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/dashboard")} style={{ color: t.textMuted }} className="hover:opacity-80">Dashboard</button>
          <div>
            <h1 className="font-bold text-lg" style={{ color: t.textPrimary }}>Clientes</h1>
            <p className="text-xs" style={{ color: t.textMuted }}>{clientes.length} clientes registrados</p>
          </div>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <button onClick={() => setMostrarColConfig(!mostrarColConfig)}
              className="px-3 py-2 rounded-lg text-sm"
              style={{ border: `1px solid ${t.border}`, color: t.textSecondary, background: t.bgCard }}>⚙️ Columnas</button>
            {mostrarColConfig && (
              <div className="absolute right-0 top-full mt-1 rounded-lg shadow-lg p-3 z-30 w-64"
                style={{ background: t.bgCard, border: `1px solid ${t.border}` }}>
                <p className="text-xs font-bold mb-2 uppercase" style={{ color: t.textMuted }}>Columnas visibles</p>
                {colConfig.filter(cc => cc.id !== "select").map(cc => {
                  const colDef = TODAS_COLUMNAS.find(c => c.id === cc.id);
                  if (!colDef) return null;
                  return (
                    <div key={cc.id}
                      draggable
                      onDragStart={(e) => onDragStartCol(e, cc.id)}
                      onDragOver={(e) => onDragOverCol(e, cc.id)}
                      onDrop={() => onDropCol(cc.id)}
                      onDragEnd={() => { setDragCol(null); setDragOver(null); }}
                      className="flex items-center gap-2 py-1.5 px-1 rounded cursor-grab active:cursor-grabbing"
                      style={{ background: dragOver === cc.id ? t.accentBlueLight : "transparent" }}>
                      <span className="text-xs" style={{ color: t.textMuted }}>⠿</span>
                      <input type="checkbox" checked={cc.visible !== false} onChange={() => toggleColumna(cc.id)} className="rounded" />
                      <span className="text-sm" style={{ color: t.textSecondary }}>{colDef.label}</span>
                    </div>
                  );
                })}
                <button onClick={() => setMostrarColConfig(false)} className="mt-2 w-full text-xs" style={{ color: t.accentBlue }}>Cerrar</button>
              </div>
            )}
          </div>
          <button onClick={() => navigate("/clientes/nuevo")}
            className="text-white px-4 py-2 rounded-lg text-sm font-semibold"
            style={{ background: t.accentBlue }}>
            + Nuevo cliente
          </button>
        </div>
      </header>

      <main className="p-4">
        {mensaje && (
          <div className="mb-3 text-sm px-4 py-3 rounded-lg flex items-center justify-between"
            style={{ background: t.accentGreenLight, border: `1px solid ${t.accentGreenBorder}`, color: t.accentGreen }}>
            <span>✅ {mensaje}</span>
            <button onClick={() => setMensaje("")} className="text-lg" style={{ color: t.accentGreen, opacity: 0.6 }}>✕</button>
          </div>
        )}

        <div className="mb-4 flex gap-3">
          <input value={busqueda} onChange={e => setBusqueda(e.target.value)}
            placeholder="Buscar por nombre, email, teléfono, población o DNI..."
            className="w-full max-w-md px-4 py-2 rounded-lg text-sm focus:outline-none"
            style={{ background: t.bgInput, border: `1px solid ${t.border}`, color: t.textPrimary }} />
          {seleccionadas.length > 0 && (
            <span className="text-sm self-center" style={{ color: t.textMuted }}>{seleccionadas.length} seleccionados</span>
          )}
        </div>

        {cargando ? (
          <div className="text-center py-20" style={{ color: t.textMuted }}>Cargando...</div>
        ) : filtrados.length === 0 ? (
          <div className="text-center py-20">
            <p className="font-semibold" style={{ color: t.textSecondary }}>No hay clientes</p>
          </div>
        ) : (
          <div className="rounded-xl overflow-x-auto" style={{ border: `2px solid ${t.border}` }}>
            <table className="w-full border-collapse">
              <thead>
                <tr style={{ borderBottom: `2px solid ${t.border}` }}>
                  {columnasVisibles.map(col => (
                    <th key={col.id} style={{ ...calcularEstilo(col.id), background: isDark ? t.bgPanel : "#f1f5f9", color: t.textMuted, borderRight: `1px solid ${t.border}` }}
                      className="text-center px-3 py-2 text-xs font-bold uppercase relative select-none last:border-r-0">
                      {col.id === "select" ? (
                        <input type="checkbox" checked={seleccionadas.length === filtrados.length && filtrados.length > 0}
                          onChange={toggleTodas} className="rounded" />
                      ) : (
                        <span>{col.label}</span>
                      )}
                      {col.id !== "select" && !col.fija && (
                        <div onMouseDown={(e) => onMouseDown(col.id, e)}
                          className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize"
                          style={{ background: "transparent" }}
                          onMouseEnter={e => { e.currentTarget.style.background = t.accentBlue; }}
                          onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }} />
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtrados.map(c => (
                  <tr key={c.id} className="cursor-pointer"
                    style={{ borderBottom: `1px solid ${t.border}`, background: t.bgCard }}
                    onMouseEnter={e => { e.currentTarget.style.background = isDark ? t.bgCardHover : t.accentBlueLight; }}
                    onMouseLeave={e => { e.currentTarget.style.background = t.bgCard; }}
                    onClick={() => navigate("/clientes/" + c.id)}>
                    {columnasVisibles.map(col => (
                      <td key={col.id} style={{ ...calcularEstilo(col.id), borderRight: `1px solid ${t.border}` }}
                        className="px-3 py-2 align-middle last:border-r-0"
                        onClick={col.id === "select" ? (e) => e.stopPropagation() : undefined}>
                        {renderCelda(c, col.id)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}